/**
 * Historical Quantities
 */
import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormGroup } from '@angular/forms';
import { NgRedux } from '@angular-redux/store';
import { ColDef } from 'ag-grid-community';
import {
  filter,
  isEqual,
  isNil,
  isEmpty,
  merge,
  sortBy,
  toLower,
  find,
  forEach,
  map,
  every,
  includes,
  set,
  reduce,
  size,
  cloneDeep,
  flow,
  pick,
  concat,
  get,
  split,
} from 'lodash';
import { Observable, BehaviorSubject } from 'rxjs';

import { DMAgGridHeaderComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-grid/header-component';

import { UI_DIMENSIONS } from '@config/ui-dimensions';
import { DecimalNumber } from '@app/shared/common';
import { IAppState } from '@app/store';
import { XUXListContainerComponent } from '@app/containers/base';
import { EventsActions, UiActions, XprtDataActions } from '@app/store/actions';
import { lazyInject } from '@app/containers/base/decorators';
import {
  IEntityConfig,
  ENTITY_TYPES,
  IProcessedEvent,
  IModalDialogResult,
  IFilterQuery,
  IDocumentOperationOptions,
  BackendApiType,
  IEvent,
  SiteForTransfer,
  IEventType,
  ISelectSiteAndEvents,
  IApiListResponse,
  IHistoricalQuantities,
  IErrorOverride,
  ToolbarButtonDefinition,
  IHistoricalQuantitiesTransfer,
  IBaseEntityType,
} from '@shared/types';
import {
  observableCombineLatest,
  observableThrowError,
  rx,
} from '@app/shared/rx';
import { getSingularEntityName } from '@app/shared/configs';
import {
  BTN_EXPORT,
  BTN_PRINT,
  BTN_SELECT_SITE_AND_EVENT,
} from '@app/text-content/button-templates';
import { XVIHeaderIconRendererComponent } from '@app/components/xvi-grid/custom-components/header-icon-renderer/header-icon-renderer.component';
import {
  generateSpacesText,
  getBaseMeasure,
  truncationText,
} from '@app/shared/utils/common-utils';
import { environment } from 'environments/environment';
import {
  IBaseXRPTResponse,
  IStandWorkSheet,
} from '@app/shared/types/entities/xrpt-entities';
import { COLUMN_ALIGN_RIGHT } from '@app/shared/constants/column-align-definition';
import { transferPDFQuantityFormatter } from '@shared/services/transfer/transfer.util';
import { setFormFieldValue } from '@app/components/form/services/schema-form/schema-form-functions';
import { sortAndFilterData } from '@app/shared/common/make-pdf/v2/utils/pdf-data-sort-filter.utils';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { getGridFilterStr } from '@app/shared/utils/grid-utils';
import {
  IPdfColumn,
  PdfGenerateServiceV2,
  ViewSupportType,
} from '@app/shared/common/make-pdf/v2';

import { BaseService, HttpErrorHandler } from '../base';
import { transferEntityType } from '../transfer/transfer.service.config';
import { TransferService } from '../transfer';
import { STATE_SELECTORS } from '../state-service';
import { XprtDataService } from '../xprt-data';
import { historicalModalDefinition } from './modal.definition';
import { PermissionService } from '../session/permission.service';
import { DmDataService } from '../dm-data/dm-data.service';
import { SitesService } from '../sites/sites.service';
import { NumberFormatService } from '../number-format/number-format.service';
import {
  EVENT_SELECTOR_SELECT,
  MAX_PAGE_SIZE,
  TIME_FORMATS,
} from '@config/global-constants';
import { DataSource, EntityServiceProvider } from '../datasource';
import { buildHistoricalQuantitiesPdfConfig } from './historical-quantities-pdf-config';
import { CompanyUtilService } from '../company/company-util.service';
import { toDate } from '@shared/utils';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import { EventsService } from '../events/events.service';

const entityType: ENTITY_TYPES = ENTITY_TYPES.HISTORICAL_QUANTITIES;
const transferServiceConfig: IEntityConfig = {
  ENTITY_TYPE: entityType,
  entityNameToCheckPermissions: transferEntityType,
  isMasterListAvailable: true,
  buttons: {},
  editor: {
    entityTitle: getSingularEntityName(entityType),
  },
  apiEndpoint: entityType,
  backendApiType: BackendApiType.RPT,
  uiRoute: entityType,
  isFormServiceSupported: false,
  list: {
    gridOptions: {
      showPagination: false,
      showViewSelector: false,
      showSearch: true,
      valueField: '_id',
      disableColumnSelector: true,
      multiselect: false,
    },
    userGridOptions: {
      enableServerSideFilter: false,
      suppressHorizontalScroll: false,
      scrollbarWidth: UI_DIMENSIONS.defaultGridScrollbarWidth,
      accentedSort: true,
      enableServerSideSorting: false,
    },
    documentQuery: {
      notNeedActive: true,
    },
    documentOperationOptions: {},
    columns: [],
    titleObs: new BehaviorSubject(null),
    class:
      'multi-level-headers fixed-scrollbar-grid historical-quantities-list',
    useCustomGrid: true,
    titleTooltipIconConfig$: new BehaviorSubject({
      tooltipConfig: {
        tooltip: I18n.InfoMessages.WillCreateTransferOnceSubmitted,
      },
    }),
  },
};

/**
 * Unhandled Transfer service that extends BaseService to use
 * basic CRUD request operations
 */
@Injectable()
export class HistoricalQuantitiesService extends BaseService {
  public static config: IEntityConfig = transferServiceConfig;
  public static ENTITY_TYPE: string = transferServiceConfig.ENTITY_TYPE;
  public iframeMode: boolean = this.stateService.getBySelector(
    STATE_SELECTORS.IFRAME_MODE,
  );

  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NgRedux) public ngRedux: NgRedux<IAppState>;
  @lazyInject(XprtDataActions) public xprtDataActions: XprtDataActions;
  @lazyInject(XprtDataService) public xprtDataService: XprtDataService;
  @lazyInject(EventsActions) public eventsActions: EventsActions;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(TransferService) public transferService: TransferService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(EntityServiceProvider)
  public entityServiceProvider: EntityServiceProvider;
  @lazyInject(PdfGenerateServiceV2)
  public pdfGenerateService: PdfGenerateServiceV2;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(XVII18nService)
  public i18nService: XVII18nService;
  @lazyInject(EventsService) public eventsService: EventsService;

  public selectedSiteAndEvents$: BehaviorSubject<ISelectSiteAndEvents> =
    new BehaviorSubject(null);
  public events: IEvent[];
  public sites: SiteForTransfer[];
  public eventTypes: IEventType[];
  public canSelectEvent$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public canFetch: boolean = false;
  public pageSize: number = 50000;
  public summaryTitleFontSize: number = 8;
  public selectedSite: SiteForTransfer = null;
  public selectedEventResult: ISelectSiteAndEvents = {
    site_id: '',
    event_type_id: '',
    event_ids: [],
    url: '',
  };
  public disabledEventIdObs$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public quickFilterFields: string[] = ['name'];

  public listProjection: string[] = ['name'];

  public headerNames: ColDef[] = [
    {
      headerName: 'Name',
      field: 'name',
      filter: false,
    },
  ];
  public xuxListContainer: XUXListContainerComponent;
  public beforeStartListMethods: Function[] = [
    this.hookXuxListContainer,
    this.getEventList,
  ];
  public siteIdDataSource: DataSource;
  public historicalInventoryItems: IHistoricalQuantitiesTransfer[] = [];
  public historicalQuantities: IHistoricalQuantities[] = [];
  public isWarehouse: boolean = false;

  public constructor(public injector: Injector) {
    super(injector);

    this.config.buttons = {
      select_site_and_event: {
        ...BTN_SELECT_SITE_AND_EVENT,
        click: this.selectSiteAndEventBtnClickHandler,
      },
      create: {
        caption: I18n.Forms.TransferForm.CreateNewTransferFromTransferQTY,
        disabledObs: new BehaviorSubject(true),
        click: this.createBtnClickHandler('transfer_qty'),
      },
      cancel_opening_stand_sheet: {
        caption: I18n.Forms.TransferForm.CreateNewTransferFromPhysicalQTY,
        click: this.createBtnClickHandler('physical_qty'),
        disabledObs: new BehaviorSubject(true),
      },
      print: {
        caption: BTN_PRINT.caption,
        click: this.print.bind(this),
        disabledObs: new BehaviorSubject(true),
      },
      export: {
        caption: BTN_EXPORT.caption,
        click: this.export.bind(this),
        disabledObs: new BehaviorSubject(true),
      },
    };
    this.config.list.documentOperationOptions = {
      continueFetchCondition: (): boolean => {
        return this.canFetch;
      },
    };
    this.config.list.columns.push(
      ...[
        {
          path: 'base',
          title: ' ',
          marryChildren: true,
          defaultView: true,
          children: [
            {
              headerName: I18n.Forms.GridForm.ItemName,
              field: 'name',
              minWidth: 300,
              width: 300,
              maxWidth: 650,
              sortable: true,
              lockVisible: true,
              pinned: 'left',
              lockPinned: true,
              lockPosition: true,
              filter: false,
              filterParams: {
                quickFilter: true,
              },
              cellClass: 'cell-wrap-text',
              headerComponentFramework: XVIHeaderIconRendererComponent,
              autoHeight: true,
              headerComponentParams: {
                displayName: this.i18nService.translate(
                  I18n.Forms.GridForm.ItemName,
                ),
                headerIconClick: this.updateEntryFormRowHeight.bind(this),
              },
            },
            {
              headerName: this.i18nService.translate(
                I18n.Forms.GridForm.Measure,
              ),
              field: 'measure',
              pinned: 'left',
              lockPinned: true,
              lockPosition: true,
              minWidth: 92,
              width: 92,
              maxWidth: 400,
              sortable: false,
              filter: false,
              lockVisible: true,
            },
          ],
        },
        {
          path: 'average',
          title: I18n.Forms.GridForm.Average,
          headerClass: 'text-center',
          marryChildren: true,
          children: [
            {
              headerName: this.i18nService.translate(
                I18n.Forms.TransferForm.TransferQty,
              ),
              field: 'average_transfer_qty',
              minWidth: 112,
              flex: 1,
              maxWidth: 550,
              sortable: true,
              lockPinned: true,
              lockPosition: true,
              lockVisible: true,
              headerComponentFramework: DMAgGridHeaderComponent,
              filter: false,
              valueFormatter:
                this.numberFormatService.getNumberFormatter(false),
              ...COLUMN_ALIGN_RIGHT,
            },
            {
              headerName: this.i18nService.translate(
                I18n.Forms.GridForm.PhysicalQty,
              ),
              field: 'average_physical_qty',
              minWidth: 124,
              maxWidth: 550,
              flex: 1,
              sortable: true,
              filter: false,
              lockPinned: true,
              lockPosition: true,
              headerComponentFramework: DMAgGridHeaderComponent,
              valueFormatter:
                this.numberFormatService.getNumberFormatter(false),
              lockVisible: true,
              ...COLUMN_ALIGN_RIGHT,
            },
          ],
        },
      ],
    );

    this.initEventsTypes();
    this.initSubscriptions();
    this.initSiteIdDataSource();
  }

  public initSiteIdDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = this.siteIdDataSourceFetchList.bind(this);
  }

  public createBtnClickHandler(type: string): Function {
    return (): void => {
      this.apiActions.clearCurrentDocument(transferEntityType);
      this.apiActions.openDocumentEditor(
        transferEntityType,
        'addHistorical',
        {
          state: {
            needBeforePageLoadPopup: false,
            action_type: type,
            historicalInventoryItems: this.historicalInventoryItems,
            site_id_to: this.selectedSite.id,
            site_name_to: this.selectedSite.name,
            store_number_to: this.selectedSite.store_number,
            isWarehouse: this.isWarehouse,
            historical_create_transfer: true,
          },
        },
        {
          showDuplicate: false,
        },
      );
    };
  }

  public async print(): Promise<void> {
    const pdfConfig = buildHistoricalQuantitiesPdfConfig(
      this.getPdfData(),
      this.getColumns(),
      !isEmpty(getGridFilterStr(this.xuxListContainer)),
    );
    await this.pdfGenerateService.printPdfAsync(pdfConfig);
  }

  public async export(): Promise<void> {
    const pdfConfig = buildHistoricalQuantitiesPdfConfig(
      this.getPdfData(),
      this.getColumns(),
      !isEmpty(getGridFilterStr(this.xuxListContainer)),
    );
    await this.pdfGenerateService.downloadPdfAsync(pdfConfig);
  }

  public getSelectedEventType(eventTypeId: string): IEventType {
    return find(
      this.eventTypes,
      (eventTypeItem) => eventTypeId === eventTypeItem.entity_id,
    );
  }

  public getColumns(): IPdfColumn[] {
    const columns: IPdfColumn[] = [
      {
        path: 'base',
        title: '',
        valueGetter: null,
        children: [
          {
            path: 'name',
            title: I18n.Forms.GridForm.ItemName,
            width: '24%',
          },
          {
            path: 'measure',
            title: I18n.Forms.GridForm.Measure,
            width: '6%',
          },
        ],
      },
    ];
    const cellWidth = 70 / (2 * this.selectedEventResult.event_ids.length + 2);
    const finalColumns: IPdfColumn[] = [
      {
        path: '',
        title: I18n.Forms.GridForm.Average,
        valueGetter: null,
        children: [
          {
            title: I18n.Forms.TransferForm.TransferQty,
            path: 'average_transfer_qty',
            alignment: 'right',
            valueFormatter: transferPDFQuantityFormatter,
            width: `${cellWidth}%`,
          },
          {
            title: I18n.Forms.GridForm.PhysicalQty,
            path: 'average_physical_qty',
            alignment: 'right',
            valueFormatter: transferPDFQuantityFormatter,
            width: `${cellWidth}%`,
          },
        ],
      },
    ];

    return concat(columns, this.getAutoColumn(cellWidth), finalColumns);
  }

  public getAutoColumn(cellWidth: number): IPdfColumn[] {
    const columns: IPdfColumn[] = [];
    forEach(this.selectedEventResult.event_ids, (eventId) => {
      const event = find(this.events, (eventItem) => eventItem.id === eventId);
      columns.push({
        path: '',
        title: event?.name,
        valueGetter: null,
        children: [
          {
            path: `${eventId}_transfer_qty`,
            title: I18n.Forms.TransferForm.TransferQty,
            valueFormatter: transferPDFQuantityFormatter,
            width: `${cellWidth}%`,
            alignment: 'right',
          },
          {
            path: `${eventId}_physical_qty`,
            title: I18n.Forms.GridForm.PhysicalQty,
            valueFormatter: transferPDFQuantityFormatter,
            alignment: 'right',
            width: `${cellWidth}%`,
          },
        ],
      });
    });

    return columns;
  }

  public getPdfData(): IBaseEntityType {
    const selectedEventType = this.getSelectedEventType(
      this.selectedEventResult.event_type_id,
    );
    const gridFilterString = getGridFilterStr(this.xuxListContainer);

    const document: IBaseEntityType = {
      gridFilterString,
      site_name: truncationText(
        this.selectedSite.original_name,
        30,
        this.summaryTitleFontSize,
      ),
      site_id: this.selectedSite.store_number,
      event_type: selectedEventType.name,
      print_date: this.companyUtilService.convertUTCDateWithTimeZone(
        toDate().toISOString(),
        true,
        TIME_FORMATS.L,
      ),
    };
    const valuePath = 'item_entity_id';
    set(
      document,
      'data_list',
      sortAndFilterData(
        this.historicalQuantities,
        {
          baseOnGrid: {
            xuxGridComponent: this.xuxListContainer.xuxGridComponent,
          } as XuxEditGridComponent,
          viewSupport: ViewSupportType.sortAndFilter,
        },
        valuePath,
        valuePath,
      ),
    );

    return document;
  }

  public siteIdDataSourceFetchList(query): Promise<IApiListResponse> {
    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        select: ['name', 'id', 'type', 'store_number', 'business_type'],
        filter_by_assigned_sites: true,
      },
      {
        ...query,
        top: MAX_PAGE_SIZE,
      },
    );

    return this.siteIdDataSource.service
      .fetchList(queryWithDefaults)
      .pipe(
        rx.first(),
        rx.map((response) => {
          response.items = map(
            sortBy(response.items, (item) => toLower(item.name)),
            (item) => ({
              ...item,
              original_name: item.name,
              name: generateSpacesText(item.name, item.store_number),
            }),
          );
          this.sites = response.items as SiteForTransfer[];
          response.total = size(response.items);

          return response;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.siteIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  private get baseUrl(): string {
    return environment.xrptBackendURI;
  }

  public hookXuxListContainer(
    xuxListContainerComponent: XUXListContainerComponent,
  ): void {
    this.xuxListContainer = xuxListContainerComponent;
  }

  /**
   * Refresh the row height of the table
   *
   * @param isShrink Pass in true to force shrink
   */
  public updateEntryFormRowHeight(isShrink?: boolean | null): void {
    const entryFormGridClassList = document.querySelector(
      '.xux-list-container',
    )?.classList;

    const isGridNameCellWrap = includes(
      entryFormGridClassList,
      'name-cell-wrap',
    );

    if (!isGridNameCellWrap || isShrink) {
      entryFormGridClassList?.add('name-cell-wrap');
    } else {
      entryFormGridClassList?.remove('name-cell-wrap');
    }
    this.xuxListContainer.gridApi.resetRowHeights();
  }

  public fetchList(
    userQuery?: IFilterQuery,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    options?: IDocumentOperationOptions,
  ): Observable<IApiListResponse> {
    const basicPath =
      '/internal/custom-reports/stand-worksheet-closing/historical-data';
    const query: IFilterQuery = merge({}, this.defaultQuery, userQuery);
    const { customParams } = query;
    const customParamsObject = {};
    forEach(customParams, (item) => {
      Object.assign(customParamsObject, {
        [item.key]: item.value,
      });
    });
    const eventIds = customParamsObject['event_ids'].split(',');

    return this.http
      .post(`${this.baseUrl}${basicPath}`, {
        event_ids: eventIds,
        site_id: customParamsObject['site_id'],
        page_size: this.pageSize,
      })
      .pipe(
        rx.map((baseXRPT: IBaseXRPTResponse) => {
          this.historicalQuantities = this.assemblyHistoricalTrans(
            baseXRPT.data as IStandWorkSheet[],
            eventIds,
          );
          // eslint-disable-next-line
          const createButtonDefinition = this.config.buttons
            .create as ToolbarButtonDefinition;
          // eslint-disable-next-line
          const printButtonDefinition = this.config.buttons
            .print as ToolbarButtonDefinition;
          // eslint-disable-next-line
          const exportButtonDefinition = this.config.buttons
            .export as ToolbarButtonDefinition;
          // eslint-disable-next-line
          const createFromPhysicalButtonDefinition = this.config.buttons
            .cancel_opening_stand_sheet as ToolbarButtonDefinition;
          const createButtonDisabled$ =
            createButtonDefinition.disabledObs as BehaviorSubject<boolean>;
          const printButtonDisabled$ =
            printButtonDefinition.disabledObs as BehaviorSubject<boolean>;
          const exportButtonDisabled$ =
            exportButtonDefinition.disabledObs as BehaviorSubject<boolean>;
          const createFromPhysicalButtonDisabled$ =
            createFromPhysicalButtonDefinition.disabledObs as BehaviorSubject<boolean>;
          if (isEmpty(this.historicalQuantities)) {
            this.selectedSiteAndEvents$.next(null);
            createButtonDisabled$.next(true);
            printButtonDisabled$.next(true);
            exportButtonDisabled$.next(true);
            createFromPhysicalButtonDisabled$.next(true);

            return {
              items: [],
              total: 0,
              current_page: 1,
              page_count: 1,
              page_size: 0,
            };
          }
          createButtonDisabled$.next(!this.canAdd);
          printButtonDisabled$.next(false);
          exportButtonDisabled$.next(false);
          createFromPhysicalButtonDisabled$.next(!this.canAdd);
          this.historicalInventoryItems = map(
            this.historicalQuantities,
            (item) => {
              return {
                ...pick(item, [
                  'name',
                  'item_entity_id',
                  'average_physical_qty',
                  'average_transfer_qty',
                ]),
                average_physical_qty: item['average_physical_qty'].valueStr,
                average_transfer_qty: item['average_transfer_qty'].valueStr,
              };
            },
          );

          return {
            items: sortBy(this.historicalQuantities, (item) =>
              toLower(item.name),
            ),
            total: size(this.historicalQuantities),
            current_page: 1,
            page_count: 1,
            page_size: size(this.historicalQuantities),
          };
        }),
        this.catchHttpError(),
      );
  }

  /* eslint-disable */
  public catchHttpError =
    (errorOverride?: IErrorOverride) =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError((httpError: HttpErrorResponse) => {
          this.selectedSiteAndEvents$.next(null);

          return observableThrowError(
            HttpErrorHandler.composeError(httpError, {
              errorOverride,
              i18nService: this.i18nService,
            }),
          );
        }),
      );

  public updateColumn(eventIds: string[]): void {
    this.config.list.columns = filter(
      this.config.list.columns,
      (column) => column.path !== 'average',
    );
    this.config.list.columns = filter(
      this.config.list.columns,
      (column) => column.path !== 'value',
    );
    forEach(eventIds, (eventId) => {
      const event = find(this.events, (eventItem) => eventItem.id === eventId);
      this.config.list.columns.push(
        ...[
          {
            path: 'value',
            headerName: event?.name,
            title: event?.name,
            headerClass: 'text-center',
            marryChildren: true,
            lockPinned: true,
            lockPosition: true,
            children: [
              {
                headerName: this.i18nService.translate(
                  I18n.Forms.TransferForm.TransferQty,
                ),
                field: `${eventId}_transfer_qty`,
                minWidth: 112,
                sortable: true,
                flex: 1,
                lockPosition: true,
                headerComponentFramework: DMAgGridHeaderComponent,
                valueFormatter:
                  this.numberFormatService.getNumberFormatter(false),
                filter: false,
                ...COLUMN_ALIGN_RIGHT,
              },
              {
                headerName: this.i18nService.translate(
                  I18n.Forms.GridForm.PhysicalQty,
                ),
                field: `${eventId}_physical_qty`,
                minWidth: 112,
                sortable: true,
                lockPosition: true,
                flex: 1,
                headerComponentFramework: DMAgGridHeaderComponent,
                valueFormatter:
                  this.numberFormatService.getNumberFormatter(false),
                filter: false,
                ...COLUMN_ALIGN_RIGHT,
              },
            ],
          },
        ],
      );
    });
    this.config.list.columns.push(
      ...[
        {
          path: 'average',
          title: I18n.Forms.GridForm.Average,
          headerClass: 'text-center',
          marryChildren: true,
          defaultView: true,
          children: [
            {
              headerName: this.i18nService.translate(
                I18n.Forms.TransferForm.TransferQty,
              ),
              field: 'average_transfer_qty',
              minWidth: 112,
              maxWidth: 400,
              width: 112,
              sortable: true,
              pinned: 'right',
              lockPinned: true,
              lockPosition: true,
              lockVisible: true,
              headerComponentFramework: DMAgGridHeaderComponent,
              valueFormatter:
                this.numberFormatService.getNumberFormatter(false),
              filter: false,
              ...COLUMN_ALIGN_RIGHT,
            },
            {
              headerName: this.i18nService.translate(
                I18n.Forms.GridForm.PhysicalQty,
              ),
              field: 'average_physical_qty',
              minWidth: 124,
              maxWidth: 400,
              width: 124,
              sortable: true,
              headerComponentFramework: DMAgGridHeaderComponent,
              valueFormatter:
                this.numberFormatService.getNumberFormatter(false),
              filter: false,
              pinned: 'right',
              lockPinned: true,
              lockPosition: true,
              lockVisible: true,
              ...COLUMN_ALIGN_RIGHT,
            },
          ],
        },
      ],
    );
    this.uiActions.reloadListConfig(Math.random());
  }

  public assemblyHistoricalTrans(
    standWorkSheets: IStandWorkSheet[],
    eventIds: string[],
  ): IHistoricalQuantities[] {
    return flow(
      this.mergeSameEventId.bind(this),
      this.filterAllEmptyData.bind(this),
      (quantities: IHistoricalQuantities[]) =>
        this.calcAverage(quantities, eventIds),
    )(standWorkSheets);
  }

  public mergeSameEventId(
    standWorkSheets: IStandWorkSheet[],
  ): IHistoricalQuantities[] {
    const quantities: IHistoricalQuantities[] = [];
    forEach(standWorkSheets, (standWorkSheet: IStandWorkSheet) => {
      const matchQuantity = find(
        quantities,
        (quantity) => quantity.item_entity_id === standWorkSheet.item_entity_id,
      );
      const baseMeasureString = getBaseMeasure(
        {
          description: get(standWorkSheet, 'measure_description'),
          factor_value: get(standWorkSheet, 'measure_factor_value'),
        },
        this.numberFormatService.getNumberFormatter(false),
        this.i18nService,
      );
      if (matchQuantity) {
        set(
          matchQuantity,
          this.newTransferId(standWorkSheet.event_id),
          new DecimalNumber(standWorkSheet.transfer_qty),
        );
        set(
          matchQuantity,
          this.newPhysicalId(standWorkSheet.event_id),
          new DecimalNumber(standWorkSheet.physical_qty),
        );
        matchQuantity.transfer_qty_event_ids.push(
          this.newTransferId(standWorkSheet.event_id),
        );
        matchQuantity.physical_qty_event_ids.push(
          this.newPhysicalId(standWorkSheet.event_id),
        );
      } else {
        quantities.push({
          ...standWorkSheet,
          [this.newTransferId(standWorkSheet.event_id)]: new DecimalNumber(
            standWorkSheet.transfer_qty,
          ),
          [this.newPhysicalId(standWorkSheet.event_id)]: new DecimalNumber(
            standWorkSheet.physical_qty,
          ),
          measure: baseMeasureString,
          transfer_qty_event_ids: [this.newTransferId(standWorkSheet.event_id)],
          physical_qty_event_ids: [this.newPhysicalId(standWorkSheet.event_id)],
          average_transfer_qty: new DecimalNumber(0),
          average_physical_qty: new DecimalNumber(0),
        });
      }

      return standWorkSheet;
    });

    return quantities;
  }

  public filterAllEmptyData(
    quantities: IHistoricalQuantities[],
  ): IHistoricalQuantities[] {
    return filter(
      quantities,
      (quantity) =>
        !(
          (every(
            quantity.transfer_qty_event_ids,
            (id) => Number(quantity[id]) === 0,
          ) &&
            every(
              quantity.physical_qty_event_ids,
              (id) => Number(quantity[id]) === 0,
            )) ||
          isEmpty(quantity.name)
        ),
    );
  }

  public calcAverage(
    quantities: IHistoricalQuantities[],
    eventIds: string[],
  ): IHistoricalQuantities[] {
    return map(quantities, (quantity) => {
      if (size(quantity.transfer_qty_event_ids) < size(eventIds)) {
        forEach(eventIds, (eventId) => {
          if (
            !includes(
              quantity.transfer_qty_event_ids,
              this.newTransferId(eventId),
            )
          ) {
            set(quantity, this.newTransferId(eventId), 0);
            set(quantity, this.newPhysicalId(eventId), 0);
            quantity.transfer_qty_event_ids.push(this.newTransferId(eventId));
            quantity.physical_qty_event_ids.push(this.newPhysicalId(eventId));
          }
        });
      }
      const allTransferQty = reduce(
        quantity.transfer_qty_event_ids,
        (pre, cur) => pre.add(quantity[cur]),
        new DecimalNumber(0),
      );
      const allPhysicalQty = reduce(
        quantity.physical_qty_event_ids,
        (pre, cur) => pre.add(quantity[cur]),
        new DecimalNumber(0),
      );
      quantity.average_physical_qty = new DecimalNumber(allPhysicalQty).div(
        size(eventIds),
      );
      quantity.average_transfer_qty = new DecimalNumber(allTransferQty).div(
        size(eventIds),
      );

      return quantity;
    });
  }

  public newTransferId(eventId: string): string {
    return `${eventId}_transfer_qty`;
  }

  public newPhysicalId(eventId: string): string {
    return `${eventId}_physical_qty`;
  }

  public selectSiteAndEventBtnClickHandler = async (): Promise<void> => {
    const result: IModalDialogResult =
      await this.openSiteAndEventSelectorModal();
    if (!result.confirmed) return;
    await this.getEventList();
    this.selectedEventResult = result.value;
    this.selectedSiteAndEvents$.next(cloneDeep(this.selectedEventResult));
    this.canFetch = true;

    return;
  };

  public async openSiteAndEventSelectorModal(): Promise<IModalDialogResult> {
    this.siteIdDataSource?.refresh();
    const modalOptions = historicalModalDefinition.selectSiteAndEventModal({
      sitesList: this.siteIdDataSource,
      eventTypeList: this.eventTypes,
      eventList: this.events,
      disabledObs: this.canSelectEvent$,
      disabledEventIdObs: this.disabledEventIdObs$,
      i18nService: this.i18nService,
    });
    const dialogResult = await this.uiActions.promptFormModal(
      merge(modalOptions, {
        onFormReady: this.popWindowFormReady(),
      }),
    );

    return {
      confirmed: dialogResult.confirmed,
      modalResult: dialogResult.modalResult,
      value: dialogResult.value,
    };
  }

  public popWindowFormReady(): Function {
    return (form: FormGroup): void => {
      observableCombineLatest([
        form.controls.site_id.valueChanges.pipe(rx.distinctUntilChanged()),
        form.controls.event_type_id.valueChanges.pipe(
          rx.distinctUntilChanged(),
        ),
      ]).subscribe(([siteId, eventTypeId]: [string, string]) => {
        this.disabledEventIdObs$.next(!(siteId && eventTypeId));
        form.get('event_ids').setValue([]);
        this.canSelectEvent$.next(true);
        let eventUrl = null;

        if (!isNil(siteId) && !isNil(eventTypeId)) {
          // eslint-disable-next-line
          eventUrl = `(availability_conditions/condition eq 'all' or (availability_conditions/condition eq 'include' and availability_conditions/sites eq in(${siteId})) or (availability_conditions/condition eq 'exclude' and availability_conditions/sites eq nin(${siteId}))) and status eq 'closed' and event_type_entity_id eq in(${eventTypeId})`;
        }
        setFormFieldValue(form, 'url', eventUrl, false);
      });
      form.controls.event_ids.valueChanges.subscribe((value) => {
        if (value && value.length) {
          this.canSelectEvent$.next(false);
          if (
            form.value.site_id &&
            form.value.event_type_id &&
            isEqual(this.selectedEventResult.site_id, form.value.site_id) &&
            isEqual(
              this.selectedEventResult.event_type_id,
              form.value.event_type_id,
            )
          ) {
            this.selectedEventResult = {
              ...this.selectedEventResult,
              event_ids: value,
            };
          }
        }
      });
      form.setValue(this.selectedEventResult);
    };
  }

  public isSiteTypeWarehouse(selectedSite: SiteForTransfer): boolean {
    return (
      isEqual(selectedSite.type, 'Warehouse') ||
      isEqual(selectedSite.business_type, 'Warehouse')
    );
  }

  public async getEventList(): Promise<void> {
    this.events = (await this.eventsService
      .fetchList({
        select: split(EVENT_SELECTOR_SELECT),
      })
      .pipe(
        rx.first(),
        rx.map((res) => get(res, 'items'))
      ).toPromise()) as IProcessedEvent[];
  }

  public initSubscriptions(): void {
    this.subscriptions.push(
      this.selectedSiteAndEvents$
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe((selectedEvent: ISelectSiteAndEvents): void => {
          if (!isNil(selectedEvent)) {
            const selectedSite = find(
              this.sites,
              (siteItem) => selectedEvent.site_id === siteItem.id,
            );
            this.selectedSite = selectedSite;
            this.isWarehouse = this.isSiteTypeWarehouse(selectedSite);
            const selectedEventType = this.getSelectedEventType(
              selectedEvent.event_type_id,
            );
            this.config.list.titleObs.next(
              `${selectedSite.name} - ${selectedEventType.name}`,
            );
            this.config.list.documentQuery = {
              ...this.config.list.documentQuery,
              customParams: [
                {
                  key: 'event_ids',
                  value: !isEmpty(selectedEvent?.event_ids)
                    ? selectedEvent?.event_ids.join(',')
                    : '',
                },
                { key: 'site_id', value: selectedEvent?.site_id },
              ],
            };
            this.updateColumn(selectedEvent?.event_ids);
          }
        }),
    );
  }

  private initEventsTypes(): void {
    this.dmDataService.getEventTypes().subscribe((eventTypes) => {
      this.eventTypes = sortBy(eventTypes, (item) => toLower(item.name));
    });
  }
  // eslint-disable-next-line max-lines
}
